import { Circle, GeoIntersection, GeoRelation, GeometryBase, Line, Point, Sphere } from "tnbase";
import { EventHandlerBase, Primitive, PrimitiveType, PythaEventDataChange, RenderItemType, TextPrimitive } from "pytha2";
import { Vector3 } from "three";
import { Glyph } from "opentype.js";

/**
 * 轴号圈半径
 */
const RADIUS = 400
/**
 * 轴号线长度
 */
const SLINE_LENGTH = 4000
/**
 * 轴号线与轴线的空余长度
 */
const SLINE_EMPTY_LENGTH = 1000
/**
 * 标注的长度
 */
const MARK_LENGTH = 2500
/**
 * 外层标注偏移距离
 */
const MARK_OFFSET_LENGTH = 800
/**
 * 标注辅助斜线X长度
 */
const MARK_HELP_X = 40
/**
 * 标注辅助斜线Y长度
 */
const MARK_HELP_Y = 140
/**
 * 轴号文字大小
 */
const AXIS_FONT_SIZE = 1000
/**
 * 标注文字大小
 */
const MARK_FONT_SIZE = 600

/**
 * 生成轴号
 */
export class EventHandlerGenGridNumberAndMark extends EventHandlerBase {

    /**
     * 处理器默认运行入口
     * @param data 
     * @returns 
     */
    run(data = {}) {
        /**
         * 步骤：
         * 1、找到向量与哪些轴线有交点
         * 2、将向量作为一个Line（P），与轴线（Q）各自求交，交点在P上，并根据t排序，
         * 3、以第一条轴线方向为x方向，获取每条轴线的两个端点投影到x方向上的点在x方向上的t，获取包围住全部轴线的t1，与t2
         * 4、根据X轴正轴角度生成SLine和SCircle  【TODO：删除已经有的轴号】，从第二个轴号开始生成标注
         * 5、根据每一个的位置生成字体
         */
        let pdList = this.node.graph.cache.pointerDownList || [];
        if (pdList?.length < 2) return false;
        const startPoint = pdList[0].point;
        const endPoint = pdList[1].point;
        let vector = new Vector3();
        vector.subVectors(endPoint, startPoint);
        // 用户点击的两点构建成一条线（P）
        const line = new Line(startPoint, endPoint);

        const storage = this.getPythaStorage();
        const activeUcs = storage.coordinateStore.activeUcs;
        const planeNormal = new Vector3(activeUcs.elements[8], activeUcs.elements[9], activeUcs.elements[10]);
        const planeAxisX = new Vector3(activeUcs.elements[0], activeUcs.elements[1], activeUcs.elements[2])
        const primitiveList: Primitive[] = Object.keys(storage.primitiveStore.primitiveDict)?.map(key => storage.primitiveStore.primitiveDict[key]) || [];
        // 找到所有的轴线（type为AxisLine的）
        const axisLinePriList = primitiveList?.filter(item => item.type === "AxisLine") || [];

        // 将用户线与轴线求交，并记录t，t是用于记录轴号生成的顺序的
        if (axisLinePriList?.length === 0) {
            return true;
        }
        // 构建
        const needAndNumberLineParamList: { t: number, line: Line }[] = [];
        axisLinePriList?.forEach((axisLine: Primitive) => {
            let axisLineWorldMatrix = axisLine.worldModelMatrix
            axisLine.geos?.forEach(geo => {
                if (geo.type === PrimitiveType.Line) {
                    const geoStart = (geo as Line).start
                    const geoEnd = (geo as Line).end
                    if (axisLineWorldMatrix) {
                        geoStart.applyMatrix4(axisLineWorldMatrix);
                        geoEnd.applyMatrix4(axisLineWorldMatrix);
                    }

                    this._line.set(startPoint, endPoint);
                    const t = GeoIntersection.LineIntersectSegmentParameter(line, geoStart, geoEnd, true, true);
                    if (t != null) {
                        // 把变换后的线加入数组 后续添加轴号
                        needAndNumberLineParamList.push({ t: t, line: new Line(geoStart, geoEnd) });
                    }
                }
            });
        })

        // 根据t升序排序，获取需要添加轴号的线
        const needAndNumberLineList = [...needAndNumberLineParamList]?.sort((a, b) => a.t - b.t)?.map(item => item.line) || [];
        if (needAndNumberLineList?.length === 0) {
            return true;
        }
        // 以第一根线为x方向，构建基准线
        let xLine = needAndNumberLineList[0].clone()
        // 旋转
        const angle = this.node.graph.cache.inputNumber['A'] || 0;
        if (angle != 0) {
            // 判断旋转点
            let xLineDir = xLine.getNormalizeX()
            if (planeNormal.clone().cross(xLineDir).dot(vector) > 0) {
                xLine.rotate(xLine.getPointAt(1), planeNormal, angle * Math.PI / 180)
            } else {
                xLine.rotate(xLine.getPointAt(0), planeNormal, angle * Math.PI / 180)
            }
        }
        let xDir = xLine.getNormalizeX()

        // 计算扫略方向 yDir
        let midZ = xDir.clone().cross(vector).normalize()
        let yDir = midZ.clone().cross(xDir).normalize()
        // 基准线上的边界点
        let borderPoint1 = new Point().copy(xLine.getPointAt(0.5)) // 尾部边界
        let borderPoint2 = new Point().copy(xLine.getPointAt(0.5)) // 头部边界

        // 将轴线的方向都扭转为基准线的方向，同时计算左右两边界点 borderPoint1, borderPoint2, 定义中间变量 midEndPoint, midClampPoint
        let midEndPoint = new Point()
        let midClampPoint = new Point()
        // 构建轴线边点与基准线的距离的map
        let lengthMap: { [key: string]: { length1: number, length2: number } } = {}
        for (let i = 0; i < needAndNumberLineList.length; i++) {
            let length1 = 0
            let length2 = 0
            if (needAndNumberLineList[i].getNormalizeX().dot(xDir) < 0) {
                needAndNumberLineList[i].negate()
            }
            // 尾部
            needAndNumberLineList[i].getPointAt(0, midEndPoint)
            // 尾部点投影到基准线上
            GeoRelation.LineClosestPointToPoint(xLine, midEndPoint, false, midClampPoint)
            length1 = midClampPoint.distanceTo(midEndPoint)
            // 判断尾部投影点与尾部边界点的方向是否与xLine方向相反，若相反，则拓展边界点
            if (new Vector3().subVectors(midClampPoint, borderPoint1).dot(xDir) < 0) {
                borderPoint1.copy(midClampPoint)
            }
            // 头部
            needAndNumberLineList[i].getPointAt(1, midEndPoint)
            // 头部点投影到基准线上
            GeoRelation.LineClosestPointToPoint(xLine, midEndPoint, false, midClampPoint)
            length2 = midClampPoint.distanceTo(midEndPoint)
            //  判断头部投影点与头部边界点的方向是否与xLine方向相同，若相同，则拓展边界点
            if (new Vector3().subVectors(midClampPoint, borderPoint2).dot(xDir) > 0) {
                borderPoint2.copy(midClampPoint)
            }
            lengthMap[i] = { length1: length1, length2: length2 }
        }

        let defaultText
        if (Math.abs(xDir.x) < Math.abs(xDir.y)) {
            defaultText = "1"
        } else {
            defaultText = "A"
        }

        const cacheText = this.node.graph.cache.inputString['N'] || defaultText;
        let gn = this.processString(cacheText);

        // 标注的尾基点端点:
        let markBasePoint1 = borderPoint1.clone().add(xDir.clone().negate().multiplyScalar(MARK_LENGTH + SLINE_EMPTY_LENGTH))
        // 标注的头基点端点:
        let markBasePoint2 = borderPoint2.clone().add(xDir.clone().multiplyScalar(MARK_LENGTH + SLINE_EMPTY_LENGTH))

        // 基准线上尾部的边界点拓展成端点
        borderPoint1.add(xDir.clone().negate().multiplyScalar(SLINE_LENGTH + SLINE_EMPTY_LENGTH))
        // 基准线上头部的边界点拓展成端点
        borderPoint2.add(xDir.clone().multiplyScalar(SLINE_LENGTH + SLINE_EMPTY_LENGTH))

        // 判断标注文字取反条件
        let markFontDir = yDir.clone()
        if (markFontDir.dot(planeAxisX) <= 0) {
            markFontDir.negate()
        }

        let markFontYDir = planeNormal.clone().cross(markFontDir)

        let endEmptyLength = SLINE_EMPTY_LENGTH
        let headEmptyLength = SLINE_EMPTY_LENGTH
        // 若是文字y方向与轴号方向同向，则尾部标注需要往下平移 MARK_OFFSET_LENGTH，反之平移头部标注
        if (markFontYDir.dot(xDir) > 0) {
            endEmptyLength += MARK_OFFSET_LENGTH
        } else {
            headEmptyLength += MARK_OFFSET_LENGTH
        }

        this.node.graph.cache.primitiveList = []
        for (let i = 0; i < needAndNumberLineList.length; i++) {

            let numberLine: Line = needAndNumberLineList[i]
            let text = gn + ""
            if (this._renderType == "string") {
                gn = this.getNextLetter(gn as string)
            } else {
                (gn as number)++
            }
            /**
             * 对于每条线，首尾分开生成，对于单独的一边：
             * 1. 获取边点坐标 EP，EP 向着对应的方向移动 SLINE_EMPTY_LENGTH 长度
             * 2. 判断边点距离基准线的垂直距离 length
             * 4. 对应的边界点向扫略方向移动距离 length ，得到点SP
             * 5. 连接 EP 与 SP ，得到第一条线SLine1
             * 8. 沿着SLine1方向移动圆圈半径R，绘制圆圈与文字，合并在一起得到轴号图元
             */
            // 尾点：
            let EP_1 = numberLine.getPointAt(0)
            EP_1.add(xDir.clone().negate().multiplyScalar(SLINE_EMPTY_LENGTH))
            let length_1 = lengthMap[i].length1
            let SP_1 = new Point().copy(borderPoint1).add(yDir.clone().multiplyScalar(length_1))
            let SLine1_1 = new Line(EP_1, SP_1)
            let center_1 = SP_1.clone().add(SLine1_1.getNormalizeX().multiplyScalar(RADIUS))
            let circle_1 = new Circle(center_1, RADIUS)
            let primitive_1 = new Primitive("MarkLine")
            this.addPrimitive(primitive_1, [SLine1_1, circle_1])
            // 轴号文字
            let endFontPrimitive = this.getFontPrimitive(text, center_1, planeAxisX, planeNormal, AXIS_FONT_SIZE, 0.7, true, true)
            if (endFontPrimitive) {
                endFontPrimitive.temporary = true;
                endFontPrimitive.colorByLayer = false;
                endFontPrimitive.layerUuid = storage.layerStore.currentLayerUuid || null;
                storage.primitiveStore.tempPrimitiveDict[endFontPrimitive.uuid] = endFontPrimitive;
                endFontPrimitive.needRedraw = true;
                this.node.graph.cache.primitiveList.push(endFontPrimitive);
            }

            // 头部
            let EP_2 = numberLine.getPointAt(1)
            EP_2.add(xDir.clone().multiplyScalar(SLINE_EMPTY_LENGTH))
            let length_2 = lengthMap[i].length2
            let SP_2 = new Point().copy(borderPoint2).add(yDir.clone().multiplyScalar(length_2))
            let SLine1_2 = new Line(EP_2, SP_2)
            let center_2 = SP_2.clone().add(SLine1_2.getNormalizeX().multiplyScalar(RADIUS))
            let circle_2 = new Circle(center_2, RADIUS)
            let primitive_2 = new Primitive("MarkLine")
            this.addPrimitive(primitive_2, [SLine1_2, circle_2])

            // 轴号文字
            let headFontPrimitive = this.getFontPrimitive(text, center_2, planeAxisX, planeNormal, AXIS_FONT_SIZE, 0.7, true, true)
            if (headFontPrimitive) {
                headFontPrimitive.temporary = true;
                headFontPrimitive.colorByLayer = false;
                headFontPrimitive.layerUuid = storage.layerStore.currentLayerUuid || null;
                storage.primitiveStore.tempPrimitiveDict[headFontPrimitive.uuid] = headFontPrimitive;
                headFontPrimitive.needRedraw = true;
                this.node.graph.cache.primitiveList.push(headFontPrimitive)
            }

            // 从第二位轴号开始，生成标注
            if (i == 0) continue;

            // 尾部标注
            let lastLength_1 = lengthMap[i - 1].length1
            // 标注两角点
            let markPoint1 = needAndNumberLineList[i - 1].getPointAt(0)
            let markPoint2 = needAndNumberLineList[i].getPointAt(0)
            let markDir = xDir.clone().negate()

            markPoint1.add(xDir.clone().negate().multiplyScalar(endEmptyLength))
            markPoint2.add(xDir.clone().negate().multiplyScalar(endEmptyLength))

            // 标注辅助点
            let MHP = markBasePoint1.clone().add(yDir.clone().multiplyScalar(lastLength_1))
            // 得到标注拉伸距离
            let markLength = MHP.distanceTo(markPoint1)

            // 获取标注图元
            let endMarkRes = this.getMarkGeosAndFontPrimitive(markPoint1, markPoint2, markDir, markLength, planeNormal, markFontDir)
            let markPrimitive1 = new Primitive("MarkLine")
            this.addPrimitive(markPrimitive1, endMarkRes.geos)
            // 尾部标注文字
            let endMarkFont = endMarkRes.fontPrimitive
            if (endMarkFont) {
                endMarkFont.temporary = true;
                endMarkFont.colorByLayer = false;
                endMarkFont.layerUuid = storage.layerStore.currentLayerUuid || null;
                storage.primitiveStore.tempPrimitiveDict[endMarkFont.uuid] = endMarkFont;
                endMarkFont.needRedraw = true;
                this.node.graph.cache.primitiveList.push(endMarkFont)
            }

            // 头部标注
            let lastLength_2 = lengthMap[i - 1].length2
            markPoint1 = needAndNumberLineList[i - 1].getPointAt(1)
            markPoint2 = needAndNumberLineList[i].getPointAt(1)
            markPoint1.add(xDir.clone().multiplyScalar(headEmptyLength))
            markPoint2.add(xDir.clone().multiplyScalar(headEmptyLength))
            // 标注辅助点
            MHP = markBasePoint2.clone().add(yDir.clone().multiplyScalar(lastLength_2))
            markLength = MHP.distanceTo(markPoint1)
            // 获取标注图元
            let headMarkRes = this.getMarkGeosAndFontPrimitive(markPoint1, markPoint2, xDir.clone(), markLength, planeNormal, markFontDir)
            let markPrimitive2 = new Primitive("MarkLine")
            this.addPrimitive(markPrimitive2, headMarkRes.geos)
            // 头部标注文字
            let headMarkFont = headMarkRes.fontPrimitive
            if (headMarkFont) {
                headMarkFont.temporary = true;
                headMarkFont.colorByLayer = false;
                headMarkFont.layerUuid = storage.layerStore.currentLayerUuid || null;
                storage.primitiveStore.tempPrimitiveDict[headMarkFont.uuid] = headMarkFont;
                headMarkFont.needRedraw = true;
                this.node.graph.cache.primitiveList.push(headMarkFont)
            }

        }

        // 添加外层标注
        if (needAndNumberLineList.length > 1) {
            let firstLine = needAndNumberLineList[0]
            let endLine = needAndNumberLineList[needAndNumberLineList.length - 1]
            // 尾部标注
            let lastLength_1 = lengthMap[0].length1
            // 标注两角点
            let markPoint1 = firstLine.getPointAt(0)
            let markPoint2 = endLine.getPointAt(0)
            markPoint1.add(xDir.clone().negate().multiplyScalar(SLINE_EMPTY_LENGTH + MARK_OFFSET_LENGTH))
            markPoint2.add(xDir.clone().negate().multiplyScalar(SLINE_EMPTY_LENGTH + MARK_OFFSET_LENGTH))

            // 标注辅助点
            let MHP = markBasePoint1.clone().add(yDir.clone().multiplyScalar(lastLength_1)).add(xDir.clone().negate().multiplyScalar(MARK_OFFSET_LENGTH))
            // 得到标注拉伸距离
            let markLength = MHP.distanceTo(markPoint1)

            // 获取标注图元
            let endMarkRes = this.getMarkGeosAndFontPrimitive(markPoint1, markPoint2, xDir.clone().negate(), markLength, planeNormal, markFontDir)
            let markPrimitive1 = new Primitive("MarkLine")
            this.addPrimitive(markPrimitive1, endMarkRes.geos)
            // 尾部标注文字
            let endMarkFont = endMarkRes.fontPrimitive
            if (endMarkFont) {
                endMarkFont.temporary = true;
                endMarkFont.colorByLayer = false;
                endMarkFont.layerUuid = storage.layerStore.currentLayerUuid || null;
                storage.primitiveStore.tempPrimitiveDict[endMarkFont.uuid] = endMarkFont;
                endMarkFont.needRedraw = true;
                this.node.graph.cache.primitiveList.push(endMarkFont)
            }

            // 头部标注
            let lastLength_2 = lengthMap[0].length2
            markPoint1 = firstLine.getPointAt(1)
            markPoint2 = endLine.getPointAt(1)
            markPoint1.add(xDir.clone().multiplyScalar(SLINE_EMPTY_LENGTH + MARK_OFFSET_LENGTH))
            markPoint2.add(xDir.clone().multiplyScalar(SLINE_EMPTY_LENGTH + MARK_OFFSET_LENGTH))
            // 标注辅助点
            MHP = markBasePoint2.clone().add(yDir.clone().multiplyScalar(lastLength_2)).add(xDir.clone().multiplyScalar(MARK_OFFSET_LENGTH))
            markLength = MHP.distanceTo(markPoint1)
            // 获取标注图元
            let headMarkRes = this.getMarkGeosAndFontPrimitive(markPoint1, markPoint2, xDir.clone(), markLength, planeNormal, markFontDir)
            let markPrimitive2 = new Primitive("MarkLine")
            this.addPrimitive(markPrimitive2, headMarkRes.geos)
            // 头部标注文字
            let headMarkFont = headMarkRes.fontPrimitive
            if (headMarkFont) {
                headMarkFont.temporary = true;
                headMarkFont.colorByLayer = false;
                headMarkFont.layerUuid = storage.layerStore.currentLayerUuid || null;
                storage.primitiveStore.tempPrimitiveDict[headMarkFont.uuid] = headMarkFont;
                headMarkFont.needRedraw = true;
                this.node.graph.cache.primitiveList.push(headMarkFont)
            }
        }

        return true;
    }

    /**
     * 生成标注图元
     * @param point1 标注角点1
     * @param point2 标注角点2
     * @param markDir 标注的方向，需要normalize
     * @param markLength 标注向着标注方向拉伸的距离，该距离是距离point1的距离
     * @param markNormal 标注所在平面的normal
     * @param fontDir 标注文字方向
     */
    getMarkGeosAndFontPrimitive(point1: Point,
        point2: Point,
        markDir: Vector3,
        markLength: number,
        markNormal: Vector3,
        fontDir: Vector3
    ) {
        /**
         * 获取标注图元步骤：
         * 1. 获取 point1 根据 markDir 方向移动 markLength 长度之后得到的点 MP1
         * 2. 获取从 point1 到 point2 的方向 VP 与距离 L
         * 3. 计算 VP 与 markDir 的夹角 angle，得到余弦cos，计算得到差值 CL = L*cos
         * 4. 得到 point2 的拉伸距离 ML2 = markLength - CL，同步骤1得到点 MP2
         * 5. 连接 point1-MP1，point2-MP2，MP1-MP2，得到三条标注线 L1,L2,L3
         * 6. 对于 MP1、MP2，分别调用 getHelpMarkLine 得到两条辅助标注线 HL1, HL2
         */
        let MP1 = new Point().copy(point1).add(markDir.clone().multiplyScalar(markLength))
        let VP = new Vector3().subVectors(point2, point1)
        let L = VP.length()
        VP.normalize()
        let angle = markDir.angleTo(VP)
        let cos = Math.cos(angle)
        let CL = L * cos
        let ML2 = markLength - CL
        let MP2 = new Point().copy(point2).add(markDir.clone().multiplyScalar(ML2))

        let L1 = new Line(point1, MP1)
        let L2 = new Line(point2, MP2)
        let L3 = new Line(MP1, MP2)

        let helpDir = new Vector3().subVectors(MP2, MP1).normalize()

        let HL1 = this.getHelpMarkLine(MP1, helpDir, markNormal, MARK_HELP_X, MARK_HELP_Y)
        let HL2 = this.getHelpMarkLine(MP2, helpDir, markNormal, MARK_HELP_X, MARK_HELP_Y)

        /**
         * 获取标注文字步骤：
         * 1. 获取文字中心 FC 为 MP1 与 MP2 的中点
         * 2. 根据 angle 与 L 获取距离 markText = L * sin(angle)
         * 3. 通过 getFontPrimitive 获取文字图元
         */
        const FC = new Point().addVectors(MP1, MP2).multiplyScalar(0.5)
        const markText = (L * Math.sin(angle)).toFixed(0)

        const markFont = this.getFontPrimitive(markText, FC, fontDir, markNormal, MARK_FONT_SIZE, 0.7, true, false)

        return {
            geos: [L1, L2, L3, ...HL1, ...HL2],
            fontPrimitive: markFont
        }

    }

    /**
     * 生成basePoint为基点的与方向yDir成45°角的辅助长方形
     * @param basePoint 
     * @param yDir 
     * @param zDir 
     * @param length 
     * @returns 
     */
    getHelpMarkLine(basePoint: Point, yDir: Vector3, zDir: Vector3, xLength: number, yLength: number) {
        let helpXDir = yDir.clone().applyAxisAngle(zDir, Math.PI / 4).normalize().multiplyScalar(yLength / 2)
        let helpYDir = zDir.clone().cross(helpXDir).normalize().multiplyScalar(xLength / 2)

        let p1 = basePoint.clone().add(helpXDir).add(helpYDir)
        let p2 = basePoint.clone().add(helpXDir).add(helpYDir.clone().negate())
        let p3 = basePoint.clone().add(helpXDir.negate()).add(helpYDir)
        let p4 = basePoint.clone().add(helpXDir).add(helpYDir.negate())

        return [new Line(p1, p2), new Line(p2, p4), new Line(p3, p4), new Line(p1, p3)]
    }

    /**
     * 初始化轴号
     * @param input 
     * @returns 
     */
    processString(input: string) {
        // 如果字符串为空，返回1
        if (!input) {
            this._renderType = 'number';
            return 1;
        }
        // 获取第一个字符
        const firstChar = input.charAt(0);
        // 判断第一个字符
        if (/\d/.test(firstChar)) {
            // 如果是数字，去除字符串中的所有非数字字符，返回格式化后的数字
            this._renderType = 'number';
            const formattedNumber = input.replace(/\D/g, ''); // 只保留数字
            return formattedNumber ? parseInt(formattedNumber, 10) : 1;
        } else if (/[a-zA-Z]/.test(firstChar)) {
            // 如果是字母，返回大写字母
            this._renderType = 'string';
            return firstChar.toUpperCase();
        } else {
            // 如果是其他字符，返回1
            this._renderType = 'number';
            return 1;
        }
    };

    // 字母轴获取下一个字母
    getNextLetter(input: string) {
        if (!input || !input.match(/^[A-Z]+$/)) {
            return;
        }

        const len = input.length;
        let carry = 1;
        let result = '';

        for (let i = len - 1; i >= 0; i--) {
            const charCode = input.charCodeAt(i) + carry;
            if (charCode > 'Z'.charCodeAt(0)) {
                result = 'A' + result;
                carry = 1;
            } else {
                result = String.fromCharCode(charCode) + result;
                carry = 0;
            }
        }

        if (carry) {
            // 处理最高位需要进位的情况
            result = 'A' + result;
        }

        return result;
    }


    /**
     * 获取文字图元
     * @param text 文字内容
     * @param basePoint 文字基点
     * @param fontDir 文字方向
     * @param fontNormal 文字平面 normal
     * @param fontSize 文字大小
     * @param widthRatio ？
     * @param horizontalCenter 文字是否水平居中
     * @param verticalCenter 文字是否垂直居中
     */
    getFontPrimitive(text: string,
        basePoint: Point,
        fontDir: Vector3,
        fontNormal: Vector3,
        fontSize: number,
        widthRatio: number,
        horizontalCenter: boolean = false,
        verticalCenter: boolean = false
    ) {
        let textPrimitive = new TextPrimitive("Text")
        const storage = this.getPythaStorage();
        const font = storage.textStore.font
        if (font == null) return null
        textPrimitive.temporary = true;
        let keys = Object.keys(textPrimitive.renderItemDict);
        if (keys.length <= 0) return null;
        let renderItem = textPrimitive.renderItemDict[keys[0]];
        renderItem.mat.color.set(0x11788F)
        textPrimitive = textPrimitive;
        textPrimitive.text = text;
        textPrimitive.fontSize = fontSize;
        textPrimitive.widthRatio = widthRatio;

        let offsetX = 0; // 文字偏移 每个文字的起点移到已有文字的最右边
        let geoIndex = 0;
        // 在局部坐标系内解析文字
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            if (char === ' ') {
                // 空格直接偏移
                offsetX += fontSize / 4;
                continue;
            }
            let glyph: Glyph = font.charToGlyph(char);
            if (glyph?.path?.commands?.length <= 0) {
                glyph = font.charToGlyph("?");
            }
            const path = glyph.getPath(0, 0, fontSize);
            const commands = path.commands || [];
            let startX = 0, startY = 0; // 起点
            let x = 0, y = 0; // 当前点
            let maxX = 0; // 解析到的最右侧坐标
            for (let cmd of commands) {
                switch (cmd.type) {
                    case 'M':
                        startX = cmd.x * widthRatio + offsetX;
                        startY = -cmd.y;
                        x = cmd.x * widthRatio + offsetX;
                        y = -cmd.y;
                        break;
                    case 'L':
                    case 'Q':
                    case 'C':
                        x = cmd.x * widthRatio + offsetX;
                        y = -cmd.y;
                        let line = new Line(new Point(startX, startY), new Point(x, y));
                        textPrimitive.geos.push(line);
                        renderItem.geoElements.push(geoIndex);
                        geoIndex++;
                        startX = x;
                        startY = y;
                        break;
                    default:
                        break;
                }
                if (maxX < x) maxX = x;
            }
            offsetX = maxX + 0; // 偏移到最右侧 + 字间距
        }

        let baseOffsetPoint = basePoint.clone()
        let fontYDir = new Vector3().copy(fontNormal).cross(fontDir).normalize()
        if (verticalCenter || horizontalCenter) {
            textPrimitive.setXYZP(new Vector3(1, 0, 0), new Vector3(0, 1, 0), new Vector3(0, 0, 1), new Point(0, 0, 0))
            let box = textPrimitive.getBoundingbox()
            if (box) {
                // 获取文字的宽度 fontWidth
                let fontWidth = box.max.x - box.min.x
                // 获取文字的高度 fontHeight
                let fontHeight = box.max.y - box.min.y
                // 将文字调整到中心点
                if (verticalCenter) {
                    baseOffsetPoint.add(fontYDir.clone().negate().multiplyScalar(fontHeight / 2 + box.min.y))
                }
                if (horizontalCenter) {
                    baseOffsetPoint.add(fontDir.clone().negate().multiplyScalar(fontWidth / 2 + box.min.x))
                }
            }
        }
        textPrimitive.setXYZP(fontDir, fontYDir, fontNormal, baseOffsetPoint)
        return textPrimitive
    }


    /**
     * 添加一个图元
     * @param primitive 
     * @param geos  
     */
    addPrimitive(primitive: Primitive, geos: GeometryBase[]) {
        const storage = this.getPythaStorage();
        primitive.geos.push(...geos);
        let keys = Object.keys(primitive.renderItemDict);
        if (keys.length > 0) {
            let renderItem = primitive.renderItemDict[keys[0]];
            renderItem.geoElements = [];
            renderItem.mat.color.set(0x11788F)
            geos.forEach(geo => {
                let geoElementsLength = renderItem.geoElements.length;
                geo.getGeoElementsIdxList().forEach(idx => {
                    renderItem.geoElements.push(idx + geoElementsLength);
                })
            });
            if (primitive.isBoolPrimitive()) {
                primitive.renderItemDict[keys[0]].geoElements = [];
                primitive.renderItemDict[keys[0]].geoElements.push(0);
            }
            renderItem.renderType = RenderItemType.LINE;
        }

        primitive.temporary = true;
        primitive.colorByLayer = false;
        primitive.layerUuid = storage.layerStore.currentLayerUuid || null;
        storage.primitiveStore.tempPrimitiveDict[primitive.uuid] = primitive;
        primitive.needRedraw = true;
        this.node.graph.cache.primitiveList.push(primitive)
    }

    // 临时线变量
    private _line = new Line();
    // 渲染的轴号是数字类型还是字符类型
    private _renderType: 'string' | 'number' = 'number';
}